// Exercises Library Page

import { query, run, saveDatabase } from '../db.js';
import { showToast, showConfirmDialog } from '../router.js';

// Muscle group color mapping
const MUSCLE_GROUP_COLORS = {
  chest: '#FF6B6B',
  back: '#4ECDC4',
  shoulders: '#95E1D3',
  legs: '#F38181',
  triceps: '#AA96DA',
  biceps: '#FCBAD3',
  core: '#FFFFD2',
  cardio: '#FF9500'
};

// Equipment types
const EQUIPMENT_TYPES = [
  'Barbell',
  'Dumbbell',
  'Kettlebell',
  'EZ Bar',
  'Trap Bar',
  'Machine',
  'Cable Machine',
  'Smith Machine',
  'Pull-up Machine',
  'Resistance Band',
  'Suspension Trainer',
  'Landmine',
  'Medicine Ball',
  'Sled',
  'Bodyweight'
];

export async function renderExercisesPage() {
  const app = document.getElementById('app');

  const exercises = query('SELECT * FROM Exercises ORDER BY musclegroup, name');
  const muscleGroups = ['chest', 'back', 'shoulders', 'legs', 'biceps', 'triceps', 'core', 'cardio'];

  app.innerHTML = `
    <div class="page safe-top">
      <div class="page-header">
        <div class="page-header-top">
          <h1 class="page-title">Exercise Library</h1>
          <button class="btn-primary btn-sm" id="add-custom-exercise-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Custom
          </button>
        </div>
        <p class="text-sm text-muted">${exercises.length} exercises available</p>
      </div>

      <!-- Search -->
      <div style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-md);">
        <input
          type="text"
          id="exercise-search"
          placeholder="Search exercises..."
          style="margin-bottom: var(--spacing-md);"
        />

        <!-- Muscle Group Filter -->
        <div style="display: flex; gap: var(--spacing-xs); overflow-x: auto; padding-bottom: var(--spacing-sm);">
          <button class="filter-chip active" data-filter="all">All</button>
          ${muscleGroups.map(mg => `
            <button class="filter-chip" data-filter="${mg}" style="text-transform: capitalize;">
              <div style="width: 12px; height: 12px; background-color: ${MUSCLE_GROUP_COLORS[mg]}; border-radius: 2px; display: inline-block; margin-right: 6px;"></div>
              ${mg}
            </button>
          `).join('')}
        </div>
      </div>

      <!-- Exercises List -->
      <div id="exercises-list" style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-xl);">
        ${renderExercisesList(exercises)}
      </div>
    </div>

    <!-- Add Custom Exercise Modal -->
    <div id="add-custom-modal" class="modal-overlay" style="display: none;">
      <div class="modal">
        <div class="modal-header">
          <h2 class="modal-title">Add Custom Exercise</h2>
          <button class="modal-close" id="close-custom-modal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Exercise Name
            </label>
            <input type="text" id="custom-name" placeholder="e.g., Bulgarian Split Squat" autofocus />
          </div>

          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Muscle Group
            </label>
            <select id="custom-musclegroup">
              ${muscleGroups.map(mg => `
                <option value="${mg}" style="text-transform: capitalize;">${mg}</option>
              `).join('')}
            </select>
          </div>

          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Equipment
            </label>
            <select id="custom-equipment">
              ${EQUIPMENT_TYPES.map(eq => `
                <option value="${eq}">${eq}</option>
              `).join('')}
            </select>
          </div>

          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Exercise Type
            </label>
            <select id="custom-exercise-type">
              <option value="weight_reps">Weight & Reps</option>
              <option value="reps_only">Reps Only (Bodyweight)</option>
              <option value="time_based">Time-Based (e.g., Plank)</option>
              <option value="cardio_jogging">Cardio - Jogging</option>
              <option value="cardio_intervals">Cardio - Sprint Intervals</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" id="cancel-custom-btn">Cancel</button>
          <button class="btn-primary" id="save-custom-btn">Add Exercise</button>
        </div>
      </div>
    </div>
  `;

  // Add filter chip styles
  const style = document.createElement('style');
  style.textContent = `
    .filter-chip {
      padding: var(--spacing-xs) var(--spacing-md);
      border-radius: var(--radius-xl);
      border: 1px solid var(--color-border);
      background: var(--color-card);
      color: var(--color-foreground);
      font-size: 0.875rem;
      cursor: pointer;
      transition: all var(--transition-base);
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
    }

    .filter-chip:hover {
      border-color: var(--color-primary);
    }

    .filter-chip.active {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: white;
    }
  `;
  document.head.appendChild(style);

  setupExerciseSearch(exercises);
  setupMuscleGroupFilter(exercises);
  setupAddCustomExercise();
  setupEditExercise();
  setupDeleteExercise();
  setupExerciseCardClick();
}

function renderExercisesList(exercises, groupByMuscle = true) {
  if (exercises.length === 0) {
    return `
      <div class="empty-state">
        <p class="text-muted">No exercises found</p>
      </div>
    `;
  }

  if (!groupByMuscle) {
    return exercises.map(ex => renderExerciseCard(ex)).join('');
  }

  let lastMuscleGroup = '';
  let html = '';

  exercises.forEach(ex => {
    if (ex.musclegroup !== lastMuscleGroup) {
      const color = MUSCLE_GROUP_COLORS[ex.musclegroup] || '#808080';
      html += `
        <h3 class="text-sm text-muted" style="margin: var(--spacing-md) 0 var(--spacing-sm); text-transform: uppercase; letter-spacing: 1px; display: flex; align-items: center; gap: var(--spacing-sm);">
          <div style="width: 16px; height: 16px; background-color: ${color}; border-radius: 3px;"></div>
          ${ex.musclegroup}
        </h3>
      `;
      lastMuscleGroup = ex.musclegroup;
    }

    html += renderExerciseCard(ex);
  });

  return html;
}

function renderExerciseCard(exercise) {
  const color = exercise.color || MUSCLE_GROUP_COLORS[exercise.musclegroup] || '#808080';

  return `
    <div class="exercise-card" data-exercise-id="${exercise.id}" style="margin-bottom: var(--spacing-sm); cursor: pointer;">
      <div class="exercise-card-header">
        <div style="display: flex; align-items: center; gap: var(--spacing-sm); flex: 1;">
          <div style="width: 20px; height: 20px; background-color: ${color}; border-radius: 4px; flex-shrink: 0;"></div>
          <div style="flex: 1;">
            <div class="exercise-name">${exercise.name}</div>
            <div class="exercise-meta">${exercise.equipment}</div>
          </div>
        </div>
        <div style="display: flex; gap: var(--spacing-xs);">
          <button class="btn-ghost btn-sm" data-edit-exercise="${exercise.id}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          <button class="btn-ghost btn-sm" data-delete-exercise="${exercise.id}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;
}

function setupExerciseSearch(allExercises) {
  const searchInput = document.getElementById('exercise-search');
  const listContainer = document.getElementById('exercises-list');

  searchInput?.addEventListener('input', () => {
    const searchTerm = searchInput.value.toLowerCase();
    const activeFilter = document.querySelector('.filter-chip.active')?.getAttribute('data-filter') || 'all';

    let filtered = allExercises;

    if (searchTerm) {
      filtered = filtered.filter(ex =>
        ex.name.toLowerCase().includes(searchTerm) ||
        ex.musclegroup.toLowerCase().includes(searchTerm) ||
        ex.equipment.toLowerCase().includes(searchTerm)
      );
    }

    if (activeFilter !== 'all') {
      filtered = filtered.filter(ex => ex.musclegroup === activeFilter);
    }

    listContainer.innerHTML = renderExercisesList(filtered, !searchTerm && activeFilter === 'all');
    setupEditExercise();
    setupDeleteExercise();
    setupExerciseCardClick();
  });
}

function setupMuscleGroupFilter(allExercises) {
  const listContainer = document.getElementById('exercises-list');
  const searchInput = document.getElementById('exercise-search');

  document.querySelectorAll('.filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      document.querySelectorAll('.filter-chip').forEach(c => c.classList.remove('active'));
      chip.classList.add('active');

      const filter = chip.getAttribute('data-filter');
      const searchTerm = searchInput?.value.toLowerCase() || '';

      let filtered = allExercises;

      if (searchTerm) {
        filtered = filtered.filter(ex =>
          ex.name.toLowerCase().includes(searchTerm) ||
          ex.musclegroup.toLowerCase().includes(searchTerm) ||
          ex.equipment.toLowerCase().includes(searchTerm)
        );
      }

      if (filter !== 'all') {
        filtered = filtered.filter(ex => ex.musclegroup === filter);
      }

      listContainer.innerHTML = renderExercisesList(filtered, !searchTerm && filter === 'all');
      setupEditExercise();
      setupDeleteExercise();
      setupExerciseCardClick();
    });
  });
}

function setupAddCustomExercise() {
  const addBtn = document.getElementById('add-custom-exercise-btn');
  const modal = document.getElementById('add-custom-modal');
  const closeBtn = document.getElementById('close-custom-modal');
  const cancelBtn = document.getElementById('cancel-custom-btn');
  const saveBtn = document.getElementById('save-custom-btn');
  const nameInput = document.getElementById('custom-name');
  const musclegroupSelect = document.getElementById('custom-musclegroup');
  const equipmentSelect = document.getElementById('custom-equipment');
  const exerciseTypeSelect = document.getElementById('custom-exercise-type');

  const openModal = () => {
    modal.style.display = 'flex';
    nameInput.value = '';
    // Reset to defaults
    equipmentSelect.selectedIndex = 0;
    exerciseTypeSelect.selectedIndex = 0; // weight_reps is default
    setTimeout(() => nameInput.focus(), 100);
  };

  const closeModal = () => {
    modal.style.display = 'none';
  };

  addBtn?.addEventListener('click', openModal);
  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  saveBtn?.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    const musclegroup = musclegroupSelect.value;
    const equipment = equipmentSelect.value;
    const exerciseType = exerciseTypeSelect.value;
    const color = MUSCLE_GROUP_COLORS[musclegroup] || '#808080';

    if (!name) {
      showToast('Please enter an exercise name', 'error');
      return;
    }

    try {
      run(
        'INSERT INTO Exercises (name, musclegroup, equipment, color, exercise_type) VALUES (?, ?, ?, ?, ?)',
        [name, musclegroup, equipment, color, exerciseType]
      );

      await saveDatabase();
      showToast('Custom exercise added', 'success');
      closeModal();
      renderExercisesPage();
    } catch (err) {
      console.error('Failed to add custom exercise:', err);
      showToast('Failed to add exercise', 'error');
    }
  });
}

function setupDeleteExercise() {
  document.querySelectorAll('[data-delete-exercise]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const exerciseId = btn.getAttribute('data-delete-exercise');
      const exercise = query('SELECT name FROM Exercises WHERE id = ?', [exerciseId])[0];

      const confirmed = await showConfirmDialog(`Are you sure you want to delete "${exercise?.name || 'this exercise'}"?`);

      if (confirmed) {
        try {
          run('DELETE FROM Exercises WHERE id = ?', [exerciseId]);
          await saveDatabase();
          showToast('Exercise deleted', 'success');
          renderExercisesPage();
        } catch (err) {
          console.error('Failed to delete exercise:', err);
          showToast('Failed to delete exercise', 'error');
        }
      }
    });
  });
}

function setupEditExercise() {
  document.querySelectorAll('[data-edit-exercise]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const exerciseId = btn.getAttribute('data-edit-exercise');
      const exercise = query('SELECT * FROM Exercises WHERE id = ?', [exerciseId])[0];

      if (!exercise) return;

      // Create edit modal
      const modal = document.createElement('div');
      modal.className = 'modal-overlay';
      modal.style.display = 'flex';
      modal.innerHTML = `
        <div class="modal">
          <div class="modal-header">
            <h2 class="modal-title">Edit Exercise</h2>
            <button class="modal-close" id="close-edit-modal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div style="margin-bottom: var(--spacing-md);">
              <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
                Exercise Name
              </label>
              <input type="text" id="edit-name" value="${exercise.name}" />
            </div>

            <div style="margin-bottom: var(--spacing-md);">
              <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
                Muscle Group
              </label>
              <select id="edit-musclegroup">
                ${['chest', 'back', 'shoulders', 'legs', 'biceps', 'triceps', 'core', 'cardio'].map(mg => `
                  <option value="${mg}" ${exercise.musclegroup === mg ? 'selected' : ''}>${mg.charAt(0).toUpperCase() + mg.slice(1)}</option>
                `).join('')}
              </select>
            </div>

            <div style="margin-bottom: var(--spacing-md);">
              <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
                Equipment
              </label>
              <select id="edit-equipment">
                ${EQUIPMENT_TYPES.map(eq => `
                  <option value="${eq}" ${exercise.equipment === eq ? 'selected' : ''}>${eq}</option>
                `).join('')}
              </select>
            </div>

            <div style="margin-bottom: var(--spacing-md);">
              <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
                Exercise Type
              </label>
              <select id="edit-exercise-type">
                <option value="weight_reps" ${(exercise.exercise_type || 'weight_reps') === 'weight_reps' ? 'selected' : ''}>Weight & Reps</option>
                <option value="reps_only" ${exercise.exercise_type === 'reps_only' ? 'selected' : ''}>Reps Only (Bodyweight)</option>
                <option value="time_based" ${exercise.exercise_type === 'time_based' ? 'selected' : ''}>Time-Based (e.g., Plank)</option>
                <option value="cardio_jogging" ${exercise.exercise_type === 'cardio_jogging' ? 'selected' : ''}>Cardio - Jogging</option>
                <option value="cardio_intervals" ${exercise.exercise_type === 'cardio_intervals' ? 'selected' : ''}>Cardio - Sprint Intervals</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" id="cancel-edit-btn">Cancel</button>
            <button class="btn-primary" id="save-edit-btn">Save Changes</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      const closeModal = () => {
        modal.remove();
      };

      modal.querySelector('#close-edit-modal').addEventListener('click', closeModal);
      modal.querySelector('#cancel-edit-btn').addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });

      modal.querySelector('#save-edit-btn').addEventListener('click', async () => {
        const name = modal.querySelector('#edit-name').value.trim();
        const musclegroup = modal.querySelector('#edit-musclegroup').value;
        const equipment = modal.querySelector('#edit-equipment').value;
        const exerciseType = modal.querySelector('#edit-exercise-type').value;
        const color = MUSCLE_GROUP_COLORS[musclegroup] || '#808080';

        if (!name) {
          showToast('Please enter an exercise name', 'error');
          return;
        }

        try {
          run(
            'UPDATE Exercises SET name = ?, musclegroup = ?, equipment = ?, color = ?, exercise_type = ? WHERE id = ?',
            [name, musclegroup, equipment, color, exerciseType, exerciseId]
          );
          await saveDatabase();
          showToast('Exercise updated', 'success');
          closeModal();
          renderExercisesPage();
        } catch (err) {
          console.error('Failed to update exercise:', err);
          showToast('Failed to update exercise', 'error');
        }
      });
    });
  });
}

function setupExerciseCardClick() {
  document.querySelectorAll('.exercise-card').forEach(card => {
    card.addEventListener('click', (e) => {
      // Don't open stats if clicking on edit or delete buttons
      if (e.target.closest('[data-edit-exercise]') || e.target.closest('[data-delete-exercise]')) {
        return;
      }

      const exerciseId = card.getAttribute('data-exercise-id');
      const exercise = query('SELECT * FROM Exercises WHERE id = ?', [exerciseId])[0];

      if (exercise) {
        openExerciseStatsModal(exercise);
      }
    });
  });
}

function openExerciseStatsModal(exercise) {
  // Fetch all performed sets for this exercise
  const performedSets = query(
    `SELECT ps.*, pw.date
     FROM PerformedSets ps
     JOIN PerformedWorkout pw ON ps.workout_instance_id = pw.id
     WHERE ps.exercise_id = ?
     ORDER BY pw.date ASC, ps.set_number ASC`,
    [exercise.id]
  );

  if (performedSets.length === 0) {
    showToast('No workout history for this exercise yet', 'info');
    return;
  }

  // Calculate stats
  const exerciseType = exercise.exercise_type || 'weight_reps';
  const stats = calculateExerciseStats(performedSets, exerciseType);

  const color = exercise.color || MUSCLE_GROUP_COLORS[exercise.musclegroup] || '#808080';

  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.style.display = 'flex';
  modal.innerHTML = `
    <div class="modal" style="max-width: 700px; max-height: 90vh; overflow-y: auto;">
      <div class="modal-header">
        <div style="display: flex; align-items: center; gap: var(--spacing-sm);">
          <div style="width: 24px; height: 24px; background-color: ${color}; border-radius: 4px;"></div>
          <div>
            <h2 class="modal-title">${exercise.name}</h2>
            <p class="text-xs text-muted" style="margin-top: 2px;">${exercise.musclegroup} · ${exercise.equipment}</p>
          </div>
        </div>
        <button class="modal-close" id="close-exercise-stats-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        ${renderExerciseStats(stats, performedSets, exerciseType)}
      </div>
      <div class="modal-footer">
        <button class="btn-primary w-full" id="close-stats-btn">Close</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const closeModal = () => {
    modal.remove();
  };

  modal.querySelector('#close-exercise-stats-modal').addEventListener('click', closeModal);
  modal.querySelector('#close-stats-btn').addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
}

function calculateExerciseStats(performedSets, exerciseType = 'weight_reps') {
  const totalSets = performedSets.length;

  // Group by workout first (needed for both types)
  const workoutGroups = {};
  performedSets.forEach(set => {
    if (!workoutGroups[set.workout_instance_id]) {
      workoutGroups[set.workout_instance_id] = {
        date: set.date,
        sets: []
      };
    }
    workoutGroups[set.workout_instance_id].sets.push(set);
  });

  const workouts = Object.values(workoutGroups);
  const sortedWorkouts = workouts.sort((a, b) => new Date(b.date) - new Date(a.date));
  const lastWorkout = sortedWorkouts[0];
  const lastWorkoutDate = lastWorkout.date;

  // Calculate frequency (workouts per week)
  const dates = workouts.map(w => new Date(w.date));
  const firstWorkout = new Date(Math.min(...dates));
  const lastWorkoutObj = new Date(Math.max(...dates));
  const daysDiff = (lastWorkoutObj - firstWorkout) / (1000 * 60 * 60 * 24);
  const weeksSpan = daysDiff / 7 || 1;
  const workoutsPerWeek = workouts.length / weeksSpan;
  const workoutsPerMonth = workouts.length / (daysDiff / 30 || 1);

  // Time-based exercises (Plank, etc.)
  if (exerciseType === 'time_based') {
    const totalDuration = performedSets.reduce((sum, set) => sum + (set.duration_seconds || 0), 0);
    const avgDuration = totalDuration / totalSets;
    const maxDuration = Math.max(...performedSets.map(s => s.duration_seconds || 0));
    const lastMaxDuration = Math.max(...lastWorkout.sets.map(s => s.duration_seconds || 0));

    const maxDurationSet = performedSets.reduce((max, set) =>
      (set.duration_seconds || 0) > (max.duration_seconds || 0) ? set : max, performedSets[0]);

    return {
      exerciseType: 'time_based',
      totalSets,
      totalDuration,
      avgDuration,
      maxDuration,
      lastMaxDuration,
      lastWorkoutDate,
      workoutsPerWeek,
      workoutsPerMonth,
      totalWorkouts: workouts.length,
      maxDurationSet,
      workouts: sortedWorkouts
    };
  }

  // Reps-based exercises (bodyweight and weighted)
  const totalReps = performedSets.reduce((sum, set) => sum + (set.reps || 0), 0);
  const totalVolume = exerciseType === 'reps_only' ? 0 :
    performedSets.reduce((sum, set) => sum + (set.weight || 0) * (set.reps || 0), 0);

  // Get weights array (excluding bodyweight sets where weight is 0 or null)
  const weights = performedSets.map(set => parseFloat(set.weight) || 0).filter(w => w > 0);
  const maxWeight = weights.length > 0 ? Math.max(...weights) : 0;
  const avgWeight = weights.length > 0 ? weights.reduce((a, b) => a + b, 0) / weights.length : 0;
  const hasWeightData = weights.length > 0;

  // Average reps per set
  const avgRepsPerSet = totalReps / totalSets;

  const avgRepsPerWorkout = workouts.reduce((sum, w) => {
    return sum + w.sets.reduce((s, set) => s + (set.reps || 0), 0);
  }, 0) / workouts.length;

  const lastMaxWeight = Math.max(...lastWorkout.sets.map(s => parseFloat(s.weight) || 0));

  // Personal records
  const maxRepsSet = performedSets.reduce((max, set) => (set.reps || 0) > (max.reps || 0) ? set : max, performedSets[0]);
  const maxVolumeSet = performedSets.reduce((max, set) => {
    const vol = (set.weight || 0) * (set.reps || 0);
    const maxVol = (max.weight || 0) * (max.reps || 0);
    return vol > maxVol ? set : max;
  }, performedSets[0]);

  return {
    exerciseType,
    totalSets,
    totalReps,
    totalVolume,
    maxWeight,
    avgWeight,
    hasWeightData,
    avgRepsPerSet,
    avgRepsPerWorkout,
    lastWorkoutDate,
    lastMaxWeight,
    workoutsPerWeek,
    workoutsPerMonth,
    totalWorkouts: workouts.length,
    maxRepsSet,
    maxVolumeSet,
    workouts: sortedWorkouts
  };
}

function renderExerciseStats(stats, performedSets, exerciseType = 'weight_reps') {
  // Time-based exercises (Plank, etc.)
  if (exerciseType === 'time_based') {
    return `
      <!-- Quick Stats Grid for Time-Based -->
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-md); margin-bottom: var(--spacing-xl);">
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Sets</div>
          <div class="stat-number">${stats.totalSets}</div>
        </div>
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Duration</div>
          <div class="stat-number">${formatSetDuration(stats.totalDuration)}</div>
        </div>
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Avg Duration</div>
          <div class="stat-number">${formatSetDuration(Math.round(stats.avgDuration))}</div>
        </div>
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Max Duration</div>
          <div class="stat-number">${formatSetDuration(stats.maxDuration)}</div>
        </div>
      </div>

      <!-- Frequency Stats -->
      <div style="margin-bottom: var(--spacing-xl);">
        <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Frequency</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--spacing-md);">
          <div class="card" style="padding: var(--spacing-md); text-align: center;">
            <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Workouts</div>
            <div class="stat-number">${stats.totalWorkouts}</div>
          </div>
          <div class="card" style="padding: var(--spacing-md); text-align: center;">
            <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Per Week</div>
            <div class="stat-number">${stats.workoutsPerWeek.toFixed(1)}</div>
          </div>
          <div class="card" style="padding: var(--spacing-md); text-align: center;">
            <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Per Month</div>
            <div class="stat-number">${stats.workoutsPerMonth.toFixed(1)}</div>
          </div>
        </div>
      </div>

      <!-- Personal Record -->
      <div style="margin-bottom: var(--spacing-xl);">
        <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Personal Record</h3>
        <div class="card" style="padding: var(--spacing-lg);">
          <div class="text-sm text-muted">Longest Hold</div>
          <div class="stat-number" style="font-size: 1.5rem; margin-top: var(--spacing-xs);">
            ${formatSetDuration(stats.maxDurationSet.duration_seconds)}
          </div>
        </div>
      </div>

      <!-- Recent Workout History -->
      <div>
        <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Recent Workouts</h3>
        <div style="max-height: 300px; overflow-y: auto;">
          ${stats.workouts.slice(0, 10).map(workout => renderWorkoutHistoryItem(workout)).join('')}
        </div>
      </div>
    `;
  }

  // Reps-based exercises (bodyweight and weighted)
  return `
    <!-- Quick Stats Grid -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-md); margin-bottom: var(--spacing-xl);">
      <div class="card" style="padding: var(--spacing-md); text-align: center;">
        <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Sets</div>
        <div class="stat-number">${stats.totalSets}</div>
      </div>
      <div class="card" style="padding: var(--spacing-md); text-align: center;">
        <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Reps</div>
        <div class="stat-number">${stats.totalReps}</div>
      </div>
      ${stats.totalVolume > 0 ? `
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Volume</div>
          <div class="stat-number">${stats.totalVolume.toFixed(0)} <span class="text-xs">kg</span></div>
        </div>
      ` : ''}
      ${stats.hasWeightData ? `
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Max Weight</div>
          <div class="stat-number">${stats.maxWeight.toFixed(1)} <span class="text-xs">kg</span></div>
        </div>
      ` : ''}
    </div>

    <!-- Secondary Stats -->
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); margin-bottom: var(--spacing-xl);">
      <div class="card" style="padding: var(--spacing-md);">
        <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Avg Reps/Set</div>
        <div class="stat-number">${stats.avgRepsPerSet.toFixed(1)}</div>
      </div>
      <div class="card" style="padding: var(--spacing-md);">
        <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Avg Reps/Workout</div>
        <div class="stat-number">${stats.avgRepsPerWorkout.toFixed(1)}</div>
      </div>
      <div class="card" style="padding: var(--spacing-md);">
        <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Last Workout</div>
        <div class="stat-number text-sm">${new Date(stats.lastWorkoutDate).toLocaleDateString()}</div>
      </div>
      ${stats.hasWeightData ? `
        <div class="card" style="padding: var(--spacing-md);">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Last Max Weight</div>
          <div class="stat-number">${stats.lastMaxWeight.toFixed(1)} <span class="text-xs">kg</span></div>
        </div>
      ` : ''}
    </div>

    <!-- Frequency Stats -->
    <div style="margin-bottom: var(--spacing-xl);">
      <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Frequency</h3>
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--spacing-md);">
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Total Workouts</div>
          <div class="stat-number">${stats.totalWorkouts}</div>
        </div>
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Per Week</div>
          <div class="stat-number">${stats.workoutsPerWeek.toFixed(1)}</div>
        </div>
        <div class="card" style="padding: var(--spacing-md); text-align: center;">
          <div class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Per Month</div>
          <div class="stat-number">${stats.workoutsPerMonth.toFixed(1)}</div>
        </div>
      </div>
    </div>

    <!-- Personal Records -->
    <div style="margin-bottom: var(--spacing-xl);">
      <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Personal Records</h3>
      <div class="card" style="padding: var(--spacing-lg);">
        ${stats.hasWeightData ? `
          <div style="margin-bottom: var(--spacing-md);">
            <div class="text-sm text-muted">Heaviest Set</div>
            <div class="stat-number" style="font-size: 1.5rem; margin-top: var(--spacing-xs);">
              ${stats.maxWeight.toFixed(1)} kg × ${performedSets.find(s => parseFloat(s.weight) === stats.maxWeight)?.reps || 0} reps
            </div>
          </div>
        ` : ''}
        <div ${stats.hasWeightData ? `style="margin-bottom: var(--spacing-md);"` : ''}>
          <div class="text-sm text-muted">Most Reps</div>
          <div class="stat-number" style="font-size: 1.5rem; margin-top: var(--spacing-xs);">
            ${stats.maxRepsSet.reps} reps${stats.hasWeightData ? ` @ ${parseFloat(stats.maxRepsSet.weight || 0).toFixed(1)} kg` : ''}
          </div>
        </div>
        ${stats.hasWeightData ? `
          <div>
            <div class="text-sm text-muted">Highest Volume Set</div>
            <div class="stat-number" style="font-size: 1.5rem; margin-top: var(--spacing-xs);">
              ${((stats.maxVolumeSet.weight || 0) * (stats.maxVolumeSet.reps || 0)).toFixed(0)} kg (${parseFloat(stats.maxVolumeSet.weight || 0).toFixed(1)} kg × ${stats.maxVolumeSet.reps} reps)
            </div>
          </div>
        ` : ''}
      </div>
    </div>

    ${stats.hasWeightData ? `
      <!-- Progress Chart -->
      <div style="margin-bottom: var(--spacing-xl);">
        <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Max Weight Progress</h3>
        ${renderExerciseProgressChart(stats.workouts)}
      </div>
    ` : ''}

    <!-- Recent Workout History -->
    <div>
      <h3 style="font-weight: 600; margin-bottom: var(--spacing-md);">Recent Workouts</h3>
      <div style="max-height: 300px; overflow-y: auto;">
        ${stats.workouts.slice(0, 10).map(workout => renderWorkoutHistoryItem(workout)).join('')}
      </div>
    </div>
  `;
}

function renderExerciseProgressChart(workouts) {
  if (workouts.length < 2) {
    return `<p class="text-sm text-muted">Need at least 2 workouts to show progress chart</p>`;
  }

  // Get max weight per workout
  const workoutMaxWeights = workouts.reverse().map(w => {
    const weights = w.sets.map(s => parseFloat(s.weight) || 0);
    return {
      date: w.date,
      maxWeight: Math.max(...weights, 0)
    };
  });

  const maxWeights = workoutMaxWeights.map(w => w.maxWeight).filter(w => w > 0);
  if (maxWeights.length === 0) {
    return `<p class="text-sm text-muted">No weighted sets to show progress</p>`;
  }

  const minWeight = Math.min(...maxWeights) - 5;
  const maxWeight = Math.max(...maxWeights) + 5;
  const range = maxWeight - minWeight;

  const chartWidth = 600;
  const chartHeight = 200;
  const padding = 40;

  const xStep = (chartWidth - padding * 2) / (workoutMaxWeights.length - 1);

  const points = workoutMaxWeights
    .map((w, index) => {
      const x = padding + index * xStep;
      const y = chartHeight - padding - ((w.maxWeight - minWeight) / range) * (chartHeight - padding * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return `
    <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="width: 100%; height: auto; background: var(--color-surface); border-radius: var(--radius-md);">
      <!-- Grid lines -->
      ${Array.from({ length: 5 }, (_, i) => {
        const y = padding + (i * (chartHeight - padding * 2)) / 4;
        const weight = (maxWeight - (i * range) / 4).toFixed(1);
        return `
          <line x1="${padding}" y1="${y}" x2="${chartWidth - padding}" y2="${y}"
                stroke="var(--color-border)" stroke-width="1" stroke-dasharray="4,4" />
          <text x="${padding - 5}" y="${y + 4}" text-anchor="end" fill="var(--color-muted)"
                style="font-size: 10px;">${weight} kg</text>
        `;
      }).join('')}

      <!-- Line -->
      <polyline points="${points}" fill="none" stroke="var(--color-primary)" stroke-width="2" />

      <!-- Points -->
      ${workoutMaxWeights.map((w, index) => {
        const x = padding + index * xStep;
        const y = chartHeight - padding - ((w.maxWeight - minWeight) / range) * (chartHeight - padding * 2);
        return `<circle cx="${x}" cy="${y}" r="4" fill="var(--color-primary)" />`;
      }).join('')}

      <!-- X-axis labels -->
      ${workoutMaxWeights.map((w, index) => {
        if (workoutMaxWeights.length > 10 && index % Math.ceil(workoutMaxWeights.length / 6) !== 0) {
          return '';
        }
        const x = padding + index * xStep;
        const date = new Date(w.date);
        const label = `${date.getMonth() + 1}/${date.getDate()}`;
        return `
          <text x="${x}" y="${chartHeight - 10}" text-anchor="middle"
                fill="var(--color-muted)" style="font-size: 10px;">${label}</text>
        `;
      }).join('')}
    </svg>
  `;
}

function renderWorkoutHistoryItem(workout) {
  const date = new Date(workout.date);
  const dateStr = date.toLocaleDateString();
  const totalReps = workout.sets.reduce((sum, s) => sum + (s.reps || 0), 0);
  const maxWeight = Math.max(...workout.sets.map(s => parseFloat(s.weight) || 0));
  const totalVolume = workout.sets.reduce((sum, s) => sum + (s.weight || 0) * (s.reps || 0), 0);

  return `
    <div style="padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-md); margin-bottom: var(--spacing-xs);">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-sm);">
        <div class="text-sm font-medium">${dateStr}</div>
        <div class="text-xs text-muted">${workout.sets.length} sets</div>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--spacing-md); text-align: center;">
        <div>
          <div class="text-xs text-muted">Max Weight</div>
          <div class="stat-number text-sm">${maxWeight.toFixed(1)} kg</div>
        </div>
        <div>
          <div class="text-xs text-muted">Total Reps</div>
          <div class="stat-number text-sm">${totalReps}</div>
        </div>
        <div>
          <div class="text-xs text-muted">Volume</div>
          <div class="stat-number text-sm">${totalVolume.toFixed(0)} kg</div>
        </div>
      </div>
    </div>
  `;
}

// Helper functions for exercise sets display
function formatSetDisplay(weight, reps, duration, exerciseType) {
  if (exerciseType === 'time_based') {
    return formatSetDuration(duration);
  }

  if (!weight || weight === 0) {
    return `${reps} reps`;
  }

  return `${weight} kg × ${reps}`;
}

function formatSetDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}
